import {
  forwardRef,
  FunctionComponent,
  KeyboardEvent as ReactKeyboardEvent,
  PropsWithoutRef,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import Icon, { registerIcon } from '../Icon';
import * as caretDownIcon from '../Icon/icons/caret-down.icon';
import * as caretUpIcon from '../Icon/icons/caret-up.icon';
import { ForwardProps } from '../../types';
import VisuallyHiddenText from '../VisuallyHiddenText';
import { useConsolidatedRef, useFocusWithin, useI18n, useUID, useElement } from '../../hooks';
import Popover from '../Popover';
import FormField from '../FormField';
import { StyledFormControl } from '../FormControl';
import Menu, { MenuProps } from '../Menu';
import Button from '../Button';
import { navigatorIsAvailable } from '../../utils';
import menuHelpers from '../Menu/helpers';
import { sameWidth } from '../Popover/modifiers';

import ComboBoxInput from './ComboBoxInput';
import ComboBoxProps from './ComboBox.types';
import { StyledComboBox } from './ComboBox.styles';

registerIcon(caretDownIcon, caretUpIcon);

const isMobile = navigatorIsAvailable && navigator.userAgent.includes('Mobile');

const ComboBox: FunctionComponent<ComboBoxProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<ComboBoxProps>, ref: Ref<HTMLDivElement>) => {
    const uid = useUID();
    const {
      value,
      required,
      id = uid,
      label,
      labelHidden,
      info,
      status,
      readOnly,
      disabled,
      mode = 'single-select',
      selected,
      onChange,
      actions,
      onFocus,
      onBlur,
      onDropdownButtonClick: onDropdownButtonClickProp,
      onClick,
      menu,
      ...restProps
    } = props;

    const t = useI18n();
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const menuRef: MenuProps['ref'] = useRef(null);
    const menuComponentId = `${id}-listbox`;

    // Force a re-render to make sure useFocusWithin has valid elements to set listeners on.
    const [, setContainerEl] = useElement();
    const containerRef = useConsolidatedRef<HTMLDivElement>(ref, setContainerEl);

    const focus = useFocusWithin([containerRef], focused => {
      if (!focused) {
        setOpen(false);
        onBlur?.(selected?.items);
      } else {
        onFocus?.(selected?.items);
        if (isMobile) {
          setTimeout(() => {
            containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 150); // delay it to let keyboard expand first
        }
      }
    });

    const onInputKeyDown = useCallback(
      (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (!menu || readOnly) return;
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            if (!open) setOpen(true);
            break;
          case 'Escape':
            e.preventDefault();
            if (open) e.stopPropagation();
            setOpen(false);
            break;
          default:
        }
      },
      [open, readOnly, menu, value]
    );

    const onDropdownButtonClick = useCallback(() => {
      onDropdownButtonClickProp?.(!open);
      setOpen(!open);
    }, [open, onDropdownButtonClickProp]);

    const dropdownButton = useMemo(() => {
      return (menu?.items?.length || onDropdownButtonClickProp || !onChange) && !readOnly ? (
        <Button
          aria-hidden='true'
          aria-label={t(
            open ? 'combobox_close_list_button_a11y' : 'combobox_open_list_button_a11y'
          )}
          icon
          variant='simple'
          onClick={onDropdownButtonClick}
          onMouseDown={(e: MouseEvent) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          disabled={disabled}
          tabIndex='-1'
        >
          <Icon name={open ? 'caret-up' : 'caret-down'} />
        </Button>
      ) : null;
    }, [onDropdownButtonClick, onChange, readOnly, open, disabled]);

    const onInputClick = useCallback(
      (e: MouseEvent) => {
        if (readOnly) return;
        setOpen(true);
        onClick?.(e);
      },
      [readOnly, onClick]
    );

    useEffect(() => {
      if (focus && menu && value && value.length > 0) {
        setOpen(true);
      }
    }, [menu, value, focus]);

    // Workaround for the following error from jsx-ast-utils, fixed in version 3.5.0.
    // The prop value with an expression type of JSXFragment could not be resolved. Please file issue to get this fixed immediately.
    const inputActions = (
      <>
        {dropdownButton}
        {actions}
      </>
    );

    const Comp = (
      <StyledComboBox ref={containerRef} as={StyledFormControl} id={`${id}-combobox`}>
        <ComboBoxInput
          ref={inputRef}
          role='combobox'
          aria-haspopup='listbox'
          aria-expanded={!readOnly && !disabled && open}
          aria-autocomplete='list'
          aria-describedby={`${id}-inputDescription`}
          selected={selected?.items}
          spellCheck={false}
          {...{
            id,
            readOnly,
            disabled,
            onChange,
            value,
            mode,
            status
          }}
          actions={inputActions}
          onRemove={selected?.onRemove}
          onClick={onInputClick}
          onKeyDown={onInputKeyDown}
          {...restProps}
        />

        <VisuallyHiddenText id={`${id}-inputDescription`}>
          {`${t('combobox_open_close')} ` && onChange ? t('combobox_search_instructions') : ''}
        </VisuallyHiddenText>

        <Popover
          show={open && menu !== undefined}
          target={containerRef.current}
          placement='bottom-start'
          modifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start']
              }
            },
            sameWidth
          ]}
          strategy={isMobile ? 'absolute' : 'fixed'}
          onMouseDown={(e: MouseEvent) => e.preventDefault()}
        >
          {menu && (
            <Menu
              ref={menuRef}
              id={menuComponentId}
              role='listbox'
              mode={mode}
              {...menu}
              items={menu.items}
              focusControlEl={inputRef.current || undefined}
              onItemClick={(itemId, e) => {
                if (mode === 'single-select') {
                  setOpen(false);
                }
                menu.onItemClick?.(itemId, e);

                const clickedItem = menuHelpers.getItem(menu.items, itemId);

                if (clickedItem?.primary && !clickedItem?.selected) {
                  selected?.onNew?.(clickedItem?.primary);
                }
              }}
              arrowNavigationUnsupported
            />
          )}
        </Popover>
      </StyledComboBox>
    );

    return label ? (
      <FormField {...{ label, labelHidden, id, info, status, required, disabled }}>
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default ComboBox;
